/**
 * Mobile Date Picker Component
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Calendar picker with month navigation
 */

import { Box, Button, IconButton, Paper, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export interface MobileDatePickerProps {
  open: boolean;
  selectedDate: string;
  viewDate: Date;
  keepTimePickerOpen: boolean;
  isIOS: boolean;
  onDateSelect: (date: Date) => void;
  onMonthChange: (direction: "prev" | "next") => void;
  onViewDateChange: (date: Date) => void;
  formatDateLocal: (date: Date) => string;
}

export default function MobileDatePicker({
  open,
  selectedDate,
  viewDate,
  keepTimePickerOpen,
  isIOS,
  onDateSelect,
  onMonthChange,
  onViewDateChange,
  formatDateLocal,
}: MobileDatePickerProps) {
  // Calendar helper function
  const getCalendarDays = (year: number, month: number): Date[] => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: Date[] = [];

    // Add days from previous month
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const prevMonthLastDay = new Date(prevYear, prevMonth + 1, 0).getDate();

    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push(new Date(prevYear, prevMonth, prevMonthLastDay - i));
    }

    // Add all days of the current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    // Add days from next month to fill the grid (always show 6 weeks = 42 days)
    const totalDays = days.length;
    const remainingDays = 42 - totalDays; // 6 weeks * 7 days = 42
    const nextMonth = month === 11 ? 0 : month + 1;
    const nextYear = month === 11 ? year + 1 : year;

    for (let day = 1; day <= remainingDays; day++) {
      days.push(new Date(nextYear, nextMonth, day));
    }

    return days;
  };

  const handleMonthChange = (direction: "prev" | "next") => {
    const newDate = new Date(viewDate);
    if (direction === "prev") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    onViewDateChange(newDate);
    onMonthChange(direction);
  };

  if (!open) return null;

  const calendarDays = getCalendarDays(viewDate.getFullYear(), viewDate.getMonth());
  const today = formatDateLocal(new Date());

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: "calc(20px + env(safe-area-inset-bottom))",
          width: "100%",
          overflow: "visible",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
          boxSizing: "border-box",
          zIndex: 1300,
          border: "none",
          borderTop: "2px solid",
          borderColor: "primary.main",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          boxShadow: (theme) => theme.palette.mode === "dark" ? "0 -4px 20px rgba(0, 0, 0, 0.4)" : "0 -4px 20px rgba(0, 0, 0, 0.15), 0 -2px 8px rgba(0, 0, 0, 0.1)",
          minHeight: "400px",
          pointerEvents: "auto",
          WebkitTransform: "translate3d(0, 0, 0)",
          transform: "translate3d(0, 0, 0)",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", p: 0, width: "100%" }}>
          {/* Month/Year Header with Navigation */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 0.75,
              flexShrink: 0,
            }}
          >
            <IconButton onClick={() => handleMonthChange("prev")} size="small" sx={{ p: 0.5 }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle2" sx={{ fontWeight: "medium", fontSize: "0.9rem" }}>
              {viewDate.toLocaleDateString("en-US", { month: "short", year: "numeric" })}
            </Typography>
            <IconButton onClick={() => handleMonthChange("next")} size="small" sx={{ p: 0.5 }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Day Names Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 0.5,
              px: 2,
              mb: 0.5,
              flexShrink: 0,
            }}
          >
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <Typography
                key={day}
                variant="caption"
                sx={{
                  textAlign: "center",
                  fontWeight: "medium",
                  color: "text.secondary",
                  py: 0.5,
                  fontSize: "0.7rem",
                }}
              >
                {day}
              </Typography>
            ))}
          </Box>

          {/* Calendar Days Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 0,
              px: 2,
              pb: 1.5,
              width: "100%",
              flexShrink: 0,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              overflow: "visible",
            }}
          >
            {calendarDays.map((day, index) => {
              const dayString = formatDateLocal(day);
              const isSelected = dayString === selectedDate;
              const shouldShowBlueSelection = isSelected && keepTimePickerOpen;
              const isToday = dayString === today;
              const isCurrentMonth = day.getMonth() === viewDate.getMonth();
              const isOtherMonth = !isCurrentMonth;

              const handleDayClick = () => {
                onDateSelect(day);
                // If clicking a day from previous/next month, switch calendar view to that month
                if (isOtherMonth) {
                  onViewDateChange(new Date(day.getFullYear(), day.getMonth(), 1));
                }
              };

              return (
                <Button
                  key={`${dayString}-${index}`}
                  onClick={handleDayClick}
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => {
                    e.stopPropagation();
                    if (!isIOS) {
                      e.preventDefault();
                    }
                    handleDayClick();
                  }}
                  sx={{
                    aspectRatio: "1",
                    minWidth: 0,
                    width: "100%",
                    p: 0,
                    borderRadius: 0,
                    fontSize: shouldShowBlueSelection ? "1rem" : "0.9rem",
                    backgroundColor: shouldShowBlueSelection ? "primary.main" : "transparent",
                    color: shouldShowBlueSelection
                      ? "primary.contrastText"
                      : isToday && isCurrentMonth
                      ? "primary.main"
                      : isCurrentMonth
                      ? "text.primary"
                      : "text.disabled",
                    fontWeight: shouldShowBlueSelection ? "bold" : isToday && isCurrentMonth ? "600" : "normal",
                    borderRight: "1px solid",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    ...(isToday && !shouldShowBlueSelection && isCurrentMonth && {
                      border: "2px solid",
                      borderColor: "primary.main",
                      zIndex: 1,
                    }),
                    "&:hover": {
                      backgroundColor: isSelected ? "primary.dark" : "action.hover",
                    },
                    ...(isOtherMonth && {
                      opacity: 0.5,
                    }),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: isSelected ? "scale(1.1)" : "scale(1)",
                    transition: "transform 0.2s ease",
                    cursor: "pointer",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  {day.getDate()}
                </Button>
              );
            })}
          </Box>
        </Box>
      </Paper>
      {/* Bottom offset area with same background color */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          height: "calc(20px + env(safe-area-inset-bottom))",
          bgcolor: "background.paper",
          zIndex: 1299,
          borderTop: "1px solid",
          borderColor: "background.paper",
        }}
      />
    </>
  );
}
